/**
 * The admin login screen (epic 076 task 03) — centered card on the design
 * system, mounted OUTSIDE the Layout route. One error line: the api's login
 * answers are deliberately generic (no field-specific hints, ADR-019 item 8),
 * and the message is surfaced as-is. On success the browser lands on the
 * originally requested path when the 401 redirect carried one, else `/` —
 * with `must_change_password` set, the forced change-password screen takes
 * the return path along instead.
 * An empty field never leaves the browser (QA round 18 #136): on submit it
 * raises a hint next to the field and the request is withheld; hints clear
 * on input. The identifier is a username, not necessarily an email (operator
 * ruling 2026-10-07), so there is no format check — the api's validation
 * stays the authority. Valid input posts exactly as before (the wire field
 * stays `email`) — the api contract is unchanged.
 * The screen also states a failed sign-out honestly (QA round 18 #139
 * ride-along): when the shell's logout navigation carries a failed
 * api-side revocation, a note says so — a clean sign-out is never claimed
 * on a failed call.
 */

import { useState, type FormEvent } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

/** In-app paths only: the return target is wrapper-generated, but a crafted
 * absolute URL in the query string must not walk the user off the admin. */
function safeReturnPath(raw: string | null): string | null {
  return raw !== null && raw.startsWith('/') && !raw.startsWith('//') ? raw : null;
}

/**
 * The sign-out outcome the shell's logout navigation carries in router
 * state (`logoutRevoked`: whether the api confirmed the revocation). Parsed
 * without trust — navigation state is arbitrary, so only a well-formed
 * boolean is read. `revoked: false` means the api-side revocation failed
 * and is stated on the screen.
 */
function readLogoutOutcome(state: unknown): { revoked: boolean } | null {
  if (typeof state !== 'object' || state === null || !('logoutRevoked' in state)) {
    return null;
  }
  if (typeof state.logoutRevoked !== 'boolean') {
    return null;
  }
  return { revoked: state.logoutRevoked };
}

/** Field-level submit hints, or null when the field would pass. The username
 * is checked trimmed — the screen posts the trimmed value, so that is what
 * the server sees; the password is checked raw, matching the api's `min(1)`. */
function usernameHint(value: string): string | null {
  return value.trim() === '' ? 'Enter your username.' : null;
}

function passwordHint(value: string): string | null {
  return value === '' ? 'Enter your password.' : null;
}

export function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [hints, setHints] = useState<{ username: string | null; password: string | null }>({
    username: null,
    password: null,
  });
  const [pending, setPending] = useState(false);
  const failedLogout = readLogoutOutcome(location.state)?.revoked === false;

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (pending) return;
    const nextHints = { username: usernameHint(username), password: passwordHint(password) };
    if (nextHints.username !== null || nextHints.password !== null) {
      setError(null);
      setHints(nextHints);
      return;
    }
    setPending(true);
    setError(null);
    try {
      const user = await login(username.trim(), password);
      const returnTo = safeReturnPath(searchParams.get('return'));
      if (user.mustChangePassword) {
        navigate(
          returnTo === null
            ? '/change-password'
            : `/change-password?return=${encodeURIComponent(returnTo)}`,
          { replace: true }
        );
      } else {
        navigate(returnTo ?? '/', { replace: true });
      }
    } catch (err) {
      setError(err instanceof ApiError && err.message ? err.message : 'Sign-in failed. Try again.');
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="grain flex min-h-screen items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex items-center justify-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent font-display text-[16px] font-bold text-white">
            T
          </div>
          <div className="leading-tight">
            <div className="font-display text-[16px] font-semibold text-ink">TradingTips</div>
            <div className="font-mono text-xs uppercase tracking-[0.16em] text-muted">
              Lead Ops Console
            </div>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Sign in</CardTitle>
            <CardDescription>Use your TradingTips admin account.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} noValidate>
              <div className="space-y-4">
                {failedLogout && (
                  <p role="alert" className="text-[13px] text-destructive">
                    Sign-out failed on the server. Your session may still be active.
                  </p>
                )}
                {error !== null && (
                  <p role="alert" className="text-[13px] text-destructive">
                    {error}
                  </p>
                )}
                <div className="space-y-2">
                  <Label htmlFor="login-username">Username</Label>
                  <Input
                    id="login-username"
                    type="text"
                    autoComplete="username"
                    value={username}
                    onChange={(e) => {
                      setUsername(e.target.value);
                      setHints((current) => ({ ...current, username: null }));
                    }}
                    aria-invalid={hints.username !== null}
                    aria-describedby={hints.username !== null ? 'login-username-hint' : undefined}
                    required
                    autoFocus
                  />
                  {hints.username !== null && (
                    <p id="login-username-hint" className="text-[13px] text-destructive">
                      {hints.username}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-password">Password</Label>
                  <Input
                    id="login-password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setHints((current) => ({ ...current, password: null }));
                    }}
                    aria-invalid={hints.password !== null}
                    aria-describedby={hints.password !== null ? 'login-password-hint' : undefined}
                    required
                  />
                  {hints.password !== null && (
                    <p id="login-password-hint" className="text-[13px] text-destructive">
                      {hints.password}
                    </p>
                  )}
                </div>
                <div className="w-full pt-1">
                  <Button type="submit" variant="primary" className="w-full" disabled={pending}>
                    {pending ? 'Signing in...' : 'Sign in'}
                  </Button>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
